import { IDenialPaymentRow } from '../../services/denials/denial-payments';
import { IDenialRemarkRow, visibleDenialRemarkRows } from '../../services/denials/denial-remark-rows';
import {
    denialsCsvColumns,
    denialsCsvFilename,
    paymentsCsvColumns,
    rejectionNote,
    rejectionsCsvColumns,
    toCsv,
} from '../../services/denials/denials-export';
import { denialRowMatches, paymentRowMatches, rejectionRowMatches, searchTerms } from '../../services/denials/denials-search';
import { IRejectionRow, rejectionHeadline } from '../../services/denials/rejection-rows';
import { DenialsWeekPreset } from '../../services/denials/report-window';
import { ROUTINE_ADJUSTMENT_LABEL } from '../../services/denials/routine-adjustments';
import { coverageCounts, CoverageFilter, coverageNote, denialCoverage, IWorkerCoverage, rejectionCoverage } from '../../services/denials/worker-coverage';
import { draftForDenialRow, draftForRejectionMessage, draftFromRule } from '../../services/denials/worker-rule-draft';
import { RemarkCodes } from '../../services/dictionary/remark-codes';
import { formatDate } from '../../services/primitives/date-utils';
import { ToastContext } from '../../services/toast/toast';
import { ButtonGroup, IButtonGroupItemProps } from '../base/button-group/button-group';
import { BaseButton } from '../base/button/button';
import { BaseCheckbox } from '../base/checkbox/checkbox';
import { BaseInput } from '../base/input/input';
import { Notice } from '../base/notice/notice';
import { BaseSelect, ISelectOption } from '../base/select/select';
import { Spinner } from '../base/spinner/spinner';
import { downloadCsvBlob } from '../data-table/components/table-export/csv-utils';
import { DataTable } from '../data-table/data-table';
import { DataTablePresenter, ITableColumn } from '../data-table/data-table.presenter';
import { TypeCountBadge, TypeFilterBar, TypeFilterChip } from '../base/type-filter-bar/type-filter-bar';
import { formatCurrency } from '../patient-statement/utils/statement-format.utils';
import { TabContainer, tabElementId } from '../tab-container/tab-container';
import { WorkerRuleEditor } from '../worker-configuration/worker-rule-editor/worker-rule-editor';
import {
    mdiArrowLeft,
    mdiChevronDown,
    mdiChevronUp,
    mdiCurrencyUsdOff,
    mdiMagnify,
    mdiOpenInNew,
    mdiRobotOutline,
    mdiSyncAlert,
    mdiTrayArrowDown,
} from '@mdi/js';
import Icon from '@mdi/react';
import { addDays, startOfDay } from 'date-fns';
import type { AutomationRule } from '../../services/worker-configuration/automation/worker-automation.types';
import type { SourceMatch } from '../worker-configuration/worker-rule-editor/worker-rule-editor';
import { getDefaultActionValue, RULE_ACTION_SELECT_LABELS, RULE_NAME_MAX_LENGTH } from '../../services/worker-configuration/automation/worker-automation.utils';
import { observer } from 'mobx-react';
import * as React from 'react';
import type { TableInstance } from 'react-table';
import { DenialsClaim } from '../../services/denials/denial-claim';
import { PAYMENT_ROW_CAP } from '../../services/denials/denials-api';
import { DenialsStore, REJECTION_STATUS_TYPES, RejectionStatusChoice } from '../../stores/denials/denials.store';
import { WorkerStore } from '../../stores/worker-configuration/worker.store';
import { ClaimDrawer } from './claim-drawer';
import * as S from './denials.styles';
import { EraCard } from './era-card';

/**
 * Denials & Rejections (FE-74, with the FE-75 worker action).
 *
 * The table shows what the adjustment report can actually answer — scope, code, the official CARC
 * text, and the number of remits carrying the code — and marks what it cannot. Total charge and Total
 * paid render as pending: the report's `amount` averages per remit instead of summing (measured on
 * production 2026-09-16, see `denial-rows.ts`), so it fills neither. The other missing columns are
 * named under the table. An empty column would read as "no data for this practice" rather than "we
 * cannot ask for this yet" (NS-3, NS-4).
 *
 * The tabs FILTER, and the report behind the table holds denials only — 835 adjustments. A rejection
 * never reaches adjudication and has no CARC, so the Rejections tab cannot reuse these rows: showing
 * them there would present denials as rejections. Until rejections arrive as codes (FE-73 · B1), that
 * tab says so, and the All tab says it holds denials only.
 *
 * The WORKER column is an action, not a status. "Configure worker" opens the rule dialog for that code
 * group; whether an existing rule already covers the group is not shown, because that claim could only
 * be checked against the platform's matcher, one claim at a time (FE-75).
 */

type DenialsTab = 'denials' | 'rejections';

const TABS = [
    { id: 'denials', label: 'Denials', icon: mdiCurrencyUsdOff },
    { id: 'rejections', label: 'Rejections', icon: mdiSyncAlert },
];

/** The one panel the tabs control: its content — the table, or the rejections note — follows the active tab. */
const TAB_PANEL_ID = 'denials-tab-panel';


/**
 * The date range control. The reworked design fixes every lookup at seven days and says so on the
 * picker — "Lookups are always 7 days. Pick the last day of the week you want." — so the choice is
 * only ever WHICH week.
 */
const THIS_WEEK_LABEL = 'This week';
const LAST_WEEK_LABEL = 'Last week';
const WEEK_BUTTONS: IButtonGroupItemProps[] = [
    { label: THIS_WEEK_LABEL, title: 'The seven days ending today.' },
    { label: LAST_WEEK_LABEL, title: 'The seven days before those.' },
];
const WEEK_PRESET_BY_LABEL: Record<string, DenialsWeekPreset> = {
    [THIS_WEEK_LABEL]: 'thisWeek',
    [LAST_WEEK_LABEL]: 'lastWeek',
};

/**
 * What the count column counts, on its header. Named "Adjustments" rather than "Claims" because that
 * is what it is: this feature's history is mislabelled counts, and the column stops that here.
 */
const ADJUSTMENTS_MEANING =
    'Service-line adjustments carrying this code with this remark set. Not claims and not remits — one claim can carry several adjustments, and this report has no claim id to count by.';

/** Why one reason code can appear on several rows. Sits on the Remark header, where the question arises. */
const REMARK_MEANING =
    'The remark codes on the service line. They describe the LINE, not this adjustment, and are not paired one-to-one with it, so one reason code appears once per distinct remark set — and once with no remark at all.';

/** Charge amount goes negative on reversals, which reads as an error unless the header says otherwise. */
const AMOUNT_MEANING =
    'The adjusted amount for this code and remark set. Negative on reversals and takebacks; rows are never netted against one another.';

/**
 * Why the coverage reads "estimated" (FE-116 v3, decision 1). The page re-applies the saved rules to
 * each row; the worker chooses its rule elsewhere, so this is our reading of the rules, not its answer.
 */
const COVERAGE_ESTIMATE =
    'Estimated from the saved worker rules, read the way the platform documents them: within a condition group a RARC is an alternative to the CARC, CARC and RARC codes must all be present, and the first rule in order wins. The worker chooses its rule on the server, so a row here can differ from what it does. A rule with a payer, provider or similar condition reads Partial: this table has no payer to check it against.';

const NO_DENIAL_RULE = 'No rule matches this combination';

const COVERAGE_CHIPS: Array<{ filter: CoverageFilter; label: string }> = [
    { filter: 'all', label: 'All' },
    { filter: 'none', label: 'Not configured' },
    { filter: 'partial', label: 'Partial' },
    { filter: 'configured', label: 'Configured' },
];

const COVERAGE_BADGE_TEXT = { none: 'NOT CONFIGURED', partial: 'PARTIAL', configured: 'CONFIGURED' };

/** `CO:45` → `CO-45`, the way an 835's CAS pair is written and the design writes it. */
const dashCode = (codes: string): string => codes.replace(':', '-');

/** `N54 — text · M15 — text`: each remark with its own official text, as the design lists them. */
const remarkLines = (remarkCodes: string[]): string =>
    remarkCodes
        .map(code => {
            const text = (RemarkCodes as Record<string, string>)[code];
            return text ? `${code} \u2014 ${text}` : code;
        })
        .join(' \u00b7 ');

/** A denials row with its estimated coverage — `undefined` while the rules are still loading. */
type DenialTableRow = IDenialRemarkRow & { coverage?: IWorkerCoverage };

/**
 * Why the per-claim columns on the Rejections tab are disabled. The grain differs from the design's
 * — a row here is a message and the claims it stopped — so this says that rather than "pending".
 */
const REJECTION_CLAIMS_MEANING =
    'How many rejected transmissions carried this message. This is the count the platform reports as REJECTED, which is broader than the X12 rejection categories \u2014 it includes \u201cnot found\u201d responses.';

const NO_REJECTION_RULE = 'No rule contains a phrase from this message';

const NOT_EVALUATED = 'The Rejection Worker only evaluates REJECTED statuses';

const STATUS_TYPE_OPTIONS: ISelectOption[] = [
    ...REJECTION_STATUS_TYPES.map(type => ({
        value: type,
        label: type.charAt(0) + type.slice(1).toLowerCase(),
    })),
    { value: 'ALL', label: 'All status types' },
];

/** A rejection row with its coverage — `undefined` while rules load, `null` when not evaluated at all. */
type RejectionTableRow = IRejectionRow & { coverage?: IWorkerCoverage | null };

/**
 * The page's search box (FE-116 feedback, 2026-10-06: it was in the earlier design and v3 dropped it). It filters
 * the table on screen; `denials-search.ts` says what each table can be searched by. Module scope, so React keeps it
 * across the page's re-renders instead of remounting it on every keystroke.
 */
const DenialsSearch: React.FC<{ value: string; placeholder: string; onChange: (value: string) => void }> = ({
    value,
    placeholder,
    onChange,
}) => (
    <S.Search>
        <Icon path={mdiMagnify} size={0.75} />
        <S.SearchInput
            type="search"
            value={value}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => onChange(event.target.value)}
        />
    </S.Search>
);

/**
 * The payer's full message under its headline, two lines until clicked. Its own component so the
 * open/closed state lives with the row rather than in the page.
 */
const ExpandableMessage: React.FC<{ message: string }> = ({ message }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <S.MessageText
            expanded={open}
            role="button"
            tabIndex={0}
            aria-expanded={open}
            title={open ? undefined : 'Show the whole message'}
            onClick={(event: React.MouseEvent): void => {
                event.stopPropagation();
                setOpen(value => !value);
            }}
            onKeyDown={(event: React.KeyboardEvent): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOpen(value => !value);
                }
            }}>
            {message}
        </S.MessageText>
    );
};

const REJECTION_CHARGE_MEANING =
    'Total charge across the claims this message stopped. Charges can overlap between different messages, so these do not add up to a practice total.';


/**
 * Why the group's worker button is disabled in the drill-down. The worker dialog configures a rule
 * from a group, and that already exists from the table's own Worker column; wiring a second entry
 * point here is part of the rule-builder work (FE-116 phase 4).
 */
/**
 * Payment statuses as the design badges them: PRIMARY, SECONDARY, DENIED. Anything else keeps its own
 * words rather than being forced into one of three — a reversal is not a primary payment.
 */
const PAYMENT_STATUS_BADGE: Record<string, { label: string; tone: 'success' | 'primary' | 'attention' | 'neutral' }> = {
    PROCESSED_AS_PRIMARY: { label: 'PRIMARY', tone: 'success' },
    PROCESSED_AS_SECONDARY: { label: 'SECONDARY', tone: 'primary' },
    PROCESSED_AS_TERTIARY: { label: 'TERTIARY', tone: 'primary' },
    PROCESSED_AS_PRIMARY_FORWARDED_TO_ADDITIONAL_PAYERS: { label: 'FORWARDED', tone: 'primary' },
    REVERSAL_OF_PREVIOUS_PAYMENT: { label: 'REVERSAL', tone: 'neutral' },
    DENIED: { label: 'DENIED', tone: 'attention' },
};

const paymentStatusBadge = (status?: string): { label: string; tone: 'success' | 'primary' | 'attention' | 'neutral' } =>
    PAYMENT_STATUS_BADGE[status || ''] || { label: (status || '\u2014').replace(/_/g, ' '), tone: 'neutral' };

/** Stable empties: a fresh `[]` per render would read to every memo below as new data. */
const NO_RULES: AutomationRule[] = [];
const NO_DENIAL_ROWS: IDenialRemarkRow[] = [];
const NO_REJECTION_ROWS: IRejectionRow[] = [];
const NO_PAYMENTS: IDenialPaymentRow[] = [];



/**
 * Why the drill-down count can differ from the row it was opened from. Both are right about different
 * things; saying so here stops the mismatch reading as a bug.
 */
const DRILL_COUNT_NOTE =
    'These are the payments dated this week that carry this code with this remark set. The count will not match the row\u2019s adjustment figure, and is not meant to: one payment can carry the code on several lines, a reversal and its reprocessing are two payments for one event, and the adjustment report may date things by a different field than the payment date used here.';

/** How a console opens a dialog: its own dialog host, called with these options. */
export interface IDenialsDialogOptions {
    onClose: () => void;
    disableBackdropClick?: boolean;
    disableEscapeKeyDown?: boolean;
    maxWidth?: 'xl';
    fullWidth?: boolean;
    renderTitle?: string;
}

/**
 * What differs between the provider and Support consoles, passed in rather than forked (FE-155; the Payers
 * pattern, GP-20/21). Write controls are each console's own existing flows.
 */
export interface IDenialsPageProps {
    store: DenialsStore;
    /** The console's own worker store — the one its Workers page edits. */
    workerStore: WorkerStore;
    dialogs: {
        showDialog: (Component: React.ReactElement, options: IDenialsDialogOptions) => void;
        hideDialog: () => void;
    };
    /** The page frame, row and card each console draws its pages with. */
    layout: {
        Page: React.ComponentType<{ children?: React.ReactNode }>;
        Row: React.ComponentType<{ children?: React.ReactNode }>;
        Card: React.ComponentType<{ size: 'large'; children?: React.ReactNode }>;
    };
    /** Demo mode has no denial data to ask for, so there is nothing to search, re-range or retry. */
    isDemo: boolean;
    /** The claim's page on this console. */
    claimHref: (claimId: string) => string;
    onEditClaim: (claimId: string) => void;
    /** The console's own task compose, opened prefilled. */
    openTaskCompose: (options: { claimId: string; defaultSubject: string; defaultBody: string }) => void;
    /** The console's own close-out dialog for the claim; `done` receives the claim as saved. */
    renderCloseout: (claim: DenialsClaim, done: (updated: DenialsClaim) => void, cancel: () => void) => React.ReactElement;
    /** Why the drawer's Convert to Private Pay is off on this console. */
    privatePayUnavailable: string;
}

export const DenialsPage: React.FC<IDenialsPageProps> = observer(
    (props: IDenialsPageProps) => {
        const { workerStore, dialogs, claimHref, onEditClaim, openTaskCompose, renderCloseout, privatePayUnavailable } = props;
        // Bound to the names the markup below was written with.
        const { Page: PageLayout, Row: PageLayoutRow, Card: DataTableCard } = props.layout;
        const [tab, setTab] = React.useState<DenialsTab>('denials');
        // Shown by default (FE-116 v3, decision 4): the first load matches the design; the toggle stays so a
        // biller can still drop the fee-schedule write-offs that are not denials.
        // Each filter has two values: what its control SHOWS (at once) and what the table APPLIES (a frame
        // later). Re-rendering the table is the slow part — measured 0.6 s for 120 rows, and ~7 s in a
        // development build — so the control has to answer before the table does, with a spinner meanwhile.
        const [shownRoutine, setShownRoutine] = React.useState(true);
        const [showRoutine, setShowRoutine] = React.useState(true);
        const [shownFilter, setShownFilter] = React.useState<CoverageFilter>('all');
        const [coverageFilter, setCoverageFilter] = React.useState<CoverageFilter>('all');
        const [filterBusy, setFilterBusy] = React.useState(false);
        // The box answers every key at once; the tables follow when React has time — re-rendering them is the
        // slow part, as with the filters above.
        const [search, setSearch] = React.useState('');
        const searchQuery = React.useDeferredValue(search);
        const terms = React.useMemo(() => searchTerms(searchQuery), [searchQuery]);
        const chooseFilter = (filter: CoverageFilter): void => {
            setShownFilter(filter);
            setFilterBusy(filter !== coverageFilter || filterBusy);
        };
        const chooseRoutine = (show: boolean): void => {
            setShownRoutine(show);
            setFilterBusy(show !== showRoutine || filterBusy);
        };
        /** Straight to "all", no spinner: used where the table is reloading anyway (tab or type change). */
        const resetFilter = (): void => {
            setShownFilter('all');
            setCoverageFilter('all');
        };
        // Apply a frame later, after the browser has painted the control's new state and the spinner.
        React.useEffect(() => {
            if (shownFilter === coverageFilter && shownRoutine === showRoutine) {
                return undefined;
            }
            let second = 0;
            const first = window.requestAnimationFrame(() => {
                second = window.requestAnimationFrame(() => {
                    setCoverageFilter(shownFilter);
                    setShowRoutine(shownRoutine);
                });
            });
            return (): void => {
                window.cancelAnimationFrame(first);
                window.cancelAnimationFrame(second);
            };
        }, [shownFilter, coverageFilter, shownRoutine, showRoutine]);
        const toastContext = React.useContext(ToastContext);

        const store: DenialsStore | undefined = props.store;

        // The saved worker rules, for the coverage column. One fetch; the Workers page shares this store.
        React.useEffect(() => {
            if (!workerStore.configuration && !workerStore.loading) {
                void workerStore.fetchConfiguration();
            }
        }, []);
        const rulesReady = !!workerStore.configuration && !workerStore.loading;
        const denialRules = rulesReady ? workerStore.getSavedAutomationRules('denial') : NO_RULES;
        const denialRulesRef = React.useRef<AutomationRule[]>(denialRules);
        denialRulesRef.current = denialRules;


        React.useEffect(() => {
            // A report that failed is asked for again when the page is opened again, not left as it was.
            if (store && !store.loading && (!store.loaded || store.error)) {
                void store.load();
            }
        }, [store]);

        /* ---- The worker rule editor (FE-116 v3 · E): Configure and Edit, on both tabs ---------- */

        /** New rules create a task the PRACTICE owns: an ENTER-owned task carries "additional fees may apply". */
        const newRuleAction = (): { type: 'CREATE_MESSAGE'; value: Record<string, unknown> } => ({
            type: 'CREATE_MESSAGE',
            value: { ...getDefaultActionValue('CREATE_MESSAGE'), messageOwner: 'PROVIDER' },
        });
        /** The store's own copy of a saved rule — already normalised for editing by its parser. */
        const editableRule = (kind: 'denial' | 'rejection', rule: AutomationRule): AutomationRule =>
            workerStore.draftAutomationWorkers[kind]?.rules.find(candidate => candidate.id === rule.id) || rule;
        const showEditor = (editor: React.ReactElement): void => {
            dialogs.showDialog(editor, { onClose: dialogs.hideDialog, disableBackdropClick: true, maxWidth: 'xl', fullWidth: true });
        };
        const onRuleSaved = (rule: AutomationRule): void => {
            dialogs.hideDialog();
            toastContext.notify && toastContext.notify(`Worker rule saved: ${rule.name}`);
        };

        const openDenialEditor = (row: DenialTableRow): void => {
            const target = { group: row.code.group, reasonCode: row.code.reasonCode, remarkCodes: row.remarkCodes };
            const coverage = denialCoverage(target, denialRulesRef.current);
            const label = `${dashCode(row.codes)}${row.remarkCodes.length ? ` \u00b7 ${row.remarkCodes.join(', ')}` : ''}`;
            const short = row.reasonDescription.split(/[.,(]/)[0].trim();
            const editing = coverage.status === 'configured' && coverage.rule ? editableRule('denial', coverage.rule) : null;
            const sourceMatch = (rule: AutomationRule): SourceMatch => {
                const result = denialCoverage(target, [rule]);
                return result.status === 'configured' ? 'full' : result.status === 'partial' && result.rule ? 'conditional' : null;
            };
            const weekRows = store ? store.rows : [];
            showEditor(
                <WorkerRuleEditor
                    store={workerStore}
                    kind="denial"
                    initial={
                        editing
                            ? draftFromRule('denial', editing)
                            : draftForDenialRow(target, `${label} \u2014 ${short}`.slice(0, RULE_NAME_MAX_LENGTH), newRuleAction())
                    }
                    sourceLabel={label}
                    matchesSource={sourceMatch}
                    preview={(rule): string => {
                        const hits = weekRows.filter(r =>
                            denialCoverage(
                                { group: r.code.group, reasonCode: r.code.reasonCode, remarkCodes: r.remarkCodes },
                                [rule],
                            ).status === 'configured',
                        );
                        const adjustments = hits.reduce((total, r) => total + r.adjustments, 0);
                        return `Matches ${hits.length} ${hits.length === 1 ? 'combination' : 'combinations'} \u00b7 ${adjustments.toLocaleString()} adjustments in this week\u2019s report (estimated)`;
                    }}
                    related={[
                        ...(coverage.status === 'partial' && coverage.rule
                            ? [{ rule: coverage.rule, why: `already covers this combination, but ${(coverage.reason || '').toLowerCase()}` }]
                            : []),
                        ...coverage.near.slice(0, 2).map(near => ({
                            rule: near.rule,
                            why: `already names this combination but also needs ${near.missing}`,
                        })),
                    ]}
                    onClose={dialogs.hideDialog}
                    onSaved={onRuleSaved}
                />,
            );
        };
        // The columns are memoised once, so their cells reach the latest handler through a ref rather than
        // a closure from the first render.
        const openDenialEditorRef = React.useRef(openDenialEditor);
        openDenialEditorRef.current = openDenialEditor;

        const openRejectionEditor = (row: IRejectionRow): void => {
            const coverage = rejectionCoverage(row.message, rejectionRulesRef.current);
            const headline = rejectionHeadline(row.message);
            const editing =
                coverage.status === 'configured' && coverage.rule ? editableRule('rejection', coverage.rule) : null;
            const weekRows = (store ? store.rejectionRows : []).filter(
                r => (r.statusType || store?.rejectionType) === 'REJECTED',
            );
            showEditor(
                <WorkerRuleEditor
                    store={workerStore}
                    kind="rejection"
                    initial={
                        editing
                            ? draftFromRule('rejection', editing)
                            : draftForRejectionMessage(row.message, headline.slice(0, RULE_NAME_MAX_LENGTH), newRuleAction())
                    }
                    sourceLabel={`\u201C${headline}\u201D`}
                    matchesSource={(rule): SourceMatch => {
                        const result = rejectionCoverage(row.message, [rule]);
                        return result.status === 'configured' ? 'full' : result.status === 'partial' && result.rule ? 'conditional' : null;
                    }}
                    preview={(rule): string => {
                        const hits = weekRows.filter(r => rejectionCoverage(r.message, [rule]).status === 'configured');
                        const count = hits.reduce((total, r) => total + r.claims, 0);
                        return `Matches ${hits.length} ${hits.length === 1 ? 'message' : 'messages'} \u00b7 ${count.toLocaleString()} rejections in this week (estimated)`;
                    }}
                    phraseSuggestion={row.statusCode ? `Code ${row.statusCode}` : undefined}
                    related={
                        coverage.status === 'partial' && coverage.rule
                            ? [
                                  {
                                      rule: coverage.rule,
                                      why: `already matches this message, but ${(coverage.reason || '').toLowerCase()}`,
                                  },
                              ]
                            : []
                    }
                    onClose={dialogs.hideDialog}
                    onSaved={onRuleSaved}
                />,
            );
        };
        const openRejectionEditorRef = React.useRef(openRejectionEditor);
        openRejectionEditorRef.current = openRejectionEditor;

        /* ---- The claim drawer's actions: each one an existing flow, reached from here ---------- */

        /** The claim page's own closeout dialog. It writes; nothing else in the drawer does. */
        const closeOutClaim = (claim: DenialsClaim): void => {
            dialogs.showDialog(
                renderCloseout(
                    claim,
                    (updated): void => {
                        store?.applyDrawerCloseout(updated);
                        dialogs.hideDialog();
                    },
                    dialogs.hideDialog,
                ),
                { onClose: dialogs.hideDialog, disableBackdropClick: true, disableEscapeKeyDown: true, renderTitle: 'Modify Closeout' },
            );
        };

        /**
         * The claim banner's own compose, prefilled with the denial. The messaging panel opens on the same
         * right edge as the drawer, so the drawer closes first rather than sit underneath it.
         */
        const createTaskForClaim = (claim: DenialsClaim): void => {
            const row = store?.drillRow;
            if (!claim.id || !row) {
                return;
            }
            store?.closeClaim();
            openTaskCompose({
                claimId: claim.id,
                defaultSubject: `Denied: ${row.codes}${row.remarkCodes.length ? ` \u00b7 ${row.remarkLabel}` : ''}`,
                defaultBody: `This claim was denied with ${row.codes} \u2014 ${row.reasonDescription}. Please review.`,
            });
        };


        const columns: ITableColumn<DenialTableRow>[] = React.useMemo(
            () => [
                {
                    id: 'CodeRemark',
                    accessor: 'codes',
                    // Widths are NUMBERS. react-table does arithmetic on them: the first version passed '110px', which
                    // became NaN, so every cell fell back to its own content width and no row lined up with another or
                    // with the headers.
                    width: 170,
                    minWidth: 170,
                    maxWidth: 170,
                    Header: () => <S.TableHeader title={REMARK_MEANING}>Code / Remark</S.TableHeader>,
                    // Separate chips, never fused: `CO-197.N54` would assert that N54 explains CO-197, which the data
                    // does not say. Jordan's v3 settled on exactly this.
                    Cell: ({ row }): React.ReactElement => (
                        <S.CodeRemarkCell>
                            <S.CodeChip>{dashCode(row.original.codes)}</S.CodeChip>
                            {row.original.remarkCodes.length ? (
                                row.original.remarkCodes.map(code => <S.RemarkChip key={code}>{code}</S.RemarkChip>)
                            ) : (
                                <S.NoRemark>no remark</S.NoRemark>
                            )}
                        </S.CodeRemarkCell>
                    ),
                },
                {
                    id: 'Reason',
                    accessor: 'reasonDescription',
                    width: 320,
                    minWidth: 320,
                    maxWidth: 4000,
                    Header: () => <S.TableHeader>Reason / Remark description</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.ReasonStack>
                            <S.ReasonMain>{row.original.reasonDescription}</S.ReasonMain>
                            {row.original.remarkCodes.length > 0 && (
                                <S.RemarkText>{remarkLines(row.original.remarkCodes)}</S.RemarkText>
                            )}
                        </S.ReasonStack>
                    ),
                },
                {
                    id: 'Count',
                    accessor: 'adjustments',
                    width: 90,
                    minWidth: 90,
                    maxWidth: 90,
                    // Adjustments, not claims and not remits: the header says so on hover.
                    Header: () => <S.TableHeaderRight title={ADJUSTMENTS_MEANING}>Count</S.TableHeaderRight>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.NumberCell>{row.original.adjustments.toLocaleString()}</S.NumberCell>
                    ),
                },
                {
                    id: 'Amount',
                    accessor: 'amount',
                    width: 150,
                    minWidth: 150,
                    maxWidth: 150,
                    Header: () => (
                        <S.TableHeaderRight title={AMOUNT_MEANING}>Adjustment amount</S.TableHeaderRight>
                    ),
                    // Signed: a reversal shows as negative rather than being netted away.
                    Cell: ({ row }): React.ReactElement => (
                        <S.NumberCell title={AMOUNT_MEANING}>
                            {formatCurrency(row.original.amount.toFixed(2))}
                        </S.NumberCell>
                    ),
                },
                {
                    id: 'Rules',
                    accessor: 'key',
                    width: 380,
                    minWidth: 380,
                    maxWidth: 380,
                    Header: () => <S.TableHeader title={COVERAGE_ESTIMATE}>Denial worker rules</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => {
                        const coverage = row.original.coverage;
                        const configured = coverage?.status === 'configured';
                        return (
                            <S.RulesCell>
                                <S.RulesText>
                                    {coverage ? (
                                        <>
                                            <S.RulesTags>
                                                <S.CoverageBadge status={coverage.status} title={COVERAGE_ESTIMATE}>
                                                    {COVERAGE_BADGE_TEXT[coverage.status]}
                                                </S.CoverageBadge>
                                                {coverage.rule && (
                                                    <S.ActionPill actionType={coverage.rule.action.type}>
                                                        {RULE_ACTION_SELECT_LABELS[coverage.rule.action.type] ||
                                                            coverage.rule.action.type}
                                                    </S.ActionPill>
                                                )}
                                            </S.RulesTags>
                                            {coverage.rule && <S.RuleName>{coverage.rule.name}</S.RuleName>}
                                            <S.RuleNote partial={coverage.status === 'partial'}>
                                                {coverageNote(coverage, denialRulesRef.current, NO_DENIAL_RULE)}
                                            </S.RuleNote>
                                        </>
                                    ) : (
                                        <S.RuleNote>Checking the worker rules&hellip;</S.RuleNote>
                                    )}
                                </S.RulesText>
                                <S.WorkerCell>
                                    <BaseButton
                                        variant={configured ? 'outlined' : 'primary'}
                                        size="compact"
                                        onClick={(event: React.MouseEvent): void => {
                                            // The row itself opens the drill-down; without this the click lands on
                                            // both, and the dialog opens over a drill-down nobody asked for.
                                            event.stopPropagation();
                                            openDenialEditorRef.current(row.original);
                                        }}>
                                        <Icon path={mdiRobotOutline} size={0.6} />
                                        <span>{configured ? 'Edit worker' : 'Configure worker'}</span>
                                    </BaseButton>
                                </S.WorkerCell>
                            </S.RulesCell>
                        );
                    },
                },
            ],
            [],
        );

        // MEMOISED, and it matters beyond speed: react-table renders every Cell with the whole table instance as
        // props, so a fresh array here on an unrelated re-render (the drawer opening, a dialog) hands every cell a
        // "changed" prop of every row. React's development build diffs and clones that for its timing track and
        // ran out of memory doing it (2026-10-05) — the dev overlay then covered the page. Data changes, not
        // renders, should reach the table.
        const storeRows = store ? store.rows : NO_DENIAL_ROWS;
        const visible = React.useMemo(() => visibleDenialRemarkRows(storeRows, showRoutine), [storeRows, showRoutine]);
        // Coverage for every visible row, then the chip filter over it — the chips count what the routine toggle
        // leaves, so the numbers on them are the rows a click would show.
        const covered: DenialTableRow[] = React.useMemo(
            () =>
                visible.map(row => ({
                    ...row,
                    coverage: rulesReady
                        ? denialCoverage(
                              { group: row.code.group, reasonCode: row.code.reasonCode, remarkCodes: row.remarkCodes },
                              denialRules,
                          )
                        : undefined,
                })),
            [visible, rulesReady, denialRules],
        );
        const counts = React.useMemo(
            () => coverageCounts(covered.filter(row => row.coverage).map(row => row.coverage!.status)),
            [covered],
        );
        // The search narrows the table and the chips; the summary above them still counts the whole week.
        const searched = React.useMemo(
            () => (terms.length ? covered.filter(row => denialRowMatches(row, terms)) : covered),
            [covered, terms],
        );
        const chipCounts = React.useMemo(
            () =>
                terms.length ? coverageCounts(searched.filter(row => row.coverage).map(row => row.coverage!.status)) : counts,
            [searched, terms, counts],
        );
        const rows = React.useMemo(
            () => (coverageFilter === 'all' ? searched : searched.filter(row => row.coverage?.status === coverageFilter)),
            [searched, coverageFilter],
        );

        // One presenter for the life of the page. Built in the JSX, a new one came with every render, each with a
        // MobX reaction nobody disposed. The rows reach it through `onDataUpdate` instead.
        const [presenter] = React.useState(
            () =>
                new DataTablePresenter<DenialTableRow>({
                    name: 'denialGroups',
                    data: rows,
                    columns,
                    filterSchemas: [],
                }),
        );

        React.useEffect(() => {
            presenter.onDataUpdate(rows);
            presenter.onDataTotalChange(rows.length);
        }, [presenter, rows]);

        /* ---- Rejections tab ---------------------------------------------------------------- */

        const rejectionColumns: ITableColumn<RejectionTableRow>[] = React.useMemo(
            () => [
                {
                    id: 'StatusMessage',
                    accessor: 'message',
                    width: 520,
                    minWidth: 520,
                    maxWidth: 4000,
                    Header: () => <S.TableHeader>Status message</S.TableHeader>,
                    // The headline only re-cuts the payer's own words; the full message is always right under it.
                    Cell: ({ row }): React.ReactElement => (
                        <S.ReasonStack>
                            <S.ReasonMain>
                                {row.original.statusType && <S.TypeTag>{row.original.statusType}</S.TypeTag>}
                                <strong>{rejectionHeadline(row.original.message)}</strong>
                            </S.ReasonMain>
                            <ExpandableMessage message={row.original.message} />
                        </S.ReasonStack>
                    ),
                },
                {
                    id: 'RejectionCount',
                    accessor: 'claims',
                    width: 90,
                    minWidth: 90,
                    maxWidth: 90,
                    Header: () => <S.TableHeaderRight title={REJECTION_CLAIMS_MEANING}>Count</S.TableHeaderRight>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.NumberCell>{row.original.claims.toLocaleString()}</S.NumberCell>
                    ),
                },
                {
                    id: 'RejectionCharge',
                    accessor: 'charge',
                    width: 140,
                    minWidth: 140,
                    maxWidth: 140,
                    Header: () => (
                        <S.TableHeaderRight title={REJECTION_CHARGE_MEANING}>Charge amount</S.TableHeaderRight>
                    ),
                    Cell: ({ row }): React.ReactElement => (
                        <S.NumberCell title={REJECTION_CHARGE_MEANING}>
                            {formatCurrency(row.original.charge.toFixed(2))}
                        </S.NumberCell>
                    ),
                },
                {
                    id: 'RejectionRules',
                    accessor: 'key',
                    width: 380,
                    minWidth: 380,
                    maxWidth: 380,
                    Header: () => <S.TableHeader title={COVERAGE_ESTIMATE}>Rejection worker rules</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => {
                        const coverage = row.original.coverage;
                        const evaluated = coverage !== null;
                        const configured = coverage?.status === 'configured';
                        // The same words the CSV export writes.
                        const note = !coverage
                            ? ''
                            : rejectionNote(row.original.message, coverage, rejectionRulesRef.current, NO_REJECTION_RULE);
                        return (
                            <S.RulesCell>
                                <S.RulesText>
                                    {coverage === undefined ? (
                                        <S.RuleNote>Checking the worker rules&hellip;</S.RuleNote>
                                    ) : coverage === null ? (
                                        <>
                                            <S.RulesTags>
                                                <S.CoverageBadge status="none">NOT EVALUATED</S.CoverageBadge>
                                            </S.RulesTags>
                                            <S.RuleNote>{NOT_EVALUATED}</S.RuleNote>
                                        </>
                                    ) : (
                                        <>
                                            <S.RulesTags>
                                                <S.CoverageBadge status={coverage.status} title={COVERAGE_ESTIMATE}>
                                                    {COVERAGE_BADGE_TEXT[coverage.status]}
                                                </S.CoverageBadge>
                                                {coverage.rule && (
                                                    <S.ActionPill actionType={coverage.rule.action.type}>
                                                        {RULE_ACTION_SELECT_LABELS[coverage.rule.action.type] ||
                                                            coverage.rule.action.type}
                                                    </S.ActionPill>
                                                )}
                                            </S.RulesTags>
                                            {coverage.rule && <S.RuleName>{coverage.rule.name}</S.RuleName>}
                                            <S.RuleNote partial={coverage.status === 'partial'}>{note}</S.RuleNote>
                                        </>
                                    )}
                                </S.RulesText>
                                <S.WorkerCell title={evaluated ? undefined : NOT_EVALUATED}>
                                    <BaseButton
                                        variant={configured ? 'outlined' : 'primary'}
                                        size="compact"
                                        disabled={!evaluated}
                                        onClick={(): void => {
                                            openRejectionEditorRef.current(row.original);
                                        }}>
                                        <Icon path={mdiRobotOutline} size={0.6} />
                                        <span>{configured ? 'Edit worker' : 'Configure worker'}</span>
                                    </BaseButton>
                                </S.WorkerCell>
                            </S.RulesCell>
                        );
                    },
                },
            ],
            [],
        );

        const rejectionRules = rulesReady ? workerStore.getSavedAutomationRules('rejection') : NO_RULES;
        const rejectionRulesRef = React.useRef<AutomationRule[]>(rejectionRules);
        rejectionRulesRef.current = rejectionRules;
        const storeRejections = store ? store.rejectionRows : NO_REJECTION_ROWS;
        const rejectionType = store?.rejectionType;
        const rejectionCovered: RejectionTableRow[] = React.useMemo(
            () =>
                storeRejections.map(row => {
                    const type = row.statusType || rejectionType;
                    return {
                        ...row,
                        coverage:
                            type !== 'REJECTED'
                                ? null
                                : rulesReady
                                ? rejectionCoverage(row.message, rejectionRules)
                                : undefined,
                    };
                }),
            [storeRejections, rejectionType, rulesReady, rejectionRules],
        );
        const rejectionEvaluated = React.useMemo(() => rejectionCovered.filter(row => row.coverage), [rejectionCovered]);
        const rejectionCounts = React.useMemo(
            () => coverageCounts(rejectionEvaluated.map(row => row.coverage!.status)),
            [rejectionEvaluated],
        );
        const rejectionSearched = React.useMemo(
            () => (terms.length ? rejectionCovered.filter(row => rejectionRowMatches(row, terms)) : rejectionCovered),
            [rejectionCovered, terms],
        );
        const rejectionChipCounts = React.useMemo(
            () =>
                terms.length
                    ? coverageCounts(rejectionSearched.filter(row => row.coverage).map(row => row.coverage!.status))
                    : rejectionCounts,
            [rejectionSearched, terms, rejectionCounts],
        );
        const rejectionRows = React.useMemo(
            () =>
                coverageFilter === 'all'
                    ? rejectionSearched
                    : rejectionSearched.filter(row => row.coverage?.status === coverageFilter),
            [rejectionSearched, coverageFilter],
        );

        const [rejectionPresenter] = React.useState(
            () =>
                new DataTablePresenter<RejectionTableRow>({
                    name: 'rejectionMessages',
                    data: rejectionRows,
                    columns: rejectionColumns,
                    filterSchemas: [],
                }),
        );

        React.useEffect(() => {
            rejectionPresenter.onDataUpdate(rejectionRows);
            rejectionPresenter.onDataTotalChange(rejectionRows.length);
        }, [rejectionPresenter, rejectionRows]);

        // The spinner goes when the browser is next idle — after the table has actually re-rendered,
        // which happens in the table's own update, not in this component's.
        React.useEffect(() => {
            if (!filterBusy || shownFilter !== coverageFilter || shownRoutine !== showRoutine) {
                return undefined;
            }
            const done = (): void => setFilterBusy(false);
            const idle = (window as Window & {
                requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
                cancelIdleCallback?: (id: number) => void;
            });
            if (idle.requestIdleCallback) {
                const id = idle.requestIdleCallback(done, { timeout: 10000 });
                return (): void => idle.cancelIdleCallback?.(id);
            }
            const id = window.setTimeout(done, 50);
            return (): void => window.clearTimeout(id);
        }, [filterBusy, rows, rejectionRows, shownFilter, coverageFilter, shownRoutine, showRoutine]);

        /* ---- Drill-down: the payments behind one denials row ------------------------------- */

        const openClaimRef = React.useRef((payment: IDenialPaymentRow): void => {
            void store?.openClaim(payment);
        });
        openClaimRef.current = (payment: IDenialPaymentRow): void => {
            void store?.openClaim(payment);
        };

        const drillColumns: ITableColumn<IDenialPaymentRow>[] = React.useMemo(
            () => [
                {
                    id: 'Expand',
                    accessor: 'id',
                    width: 40,
                    minWidth: 40,
                    maxWidth: 40,
                    Header: () => <span />,
                    Cell: ({ row }): React.ReactElement => (
                        <S.Chevron aria-hidden>
                            <Icon path={(row as unknown as { isExpanded?: boolean }).isExpanded ? mdiChevronUp : mdiChevronDown} size={0.75} />
                        </S.Chevron>
                    ),
                },
                {
                    id: 'PaymentDate',
                    accessor: 'paymentDate',
                    width: 130,
                    minWidth: 130,
                    maxWidth: 130,
                    Header: () => <S.TableHeader>Payment date</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.TableCell>
                            {row.original.paymentDate ? formatDate(row.original.paymentDate, 'MMM d, yyyy') : '\u2014'}
                            {row.original.receivedDate ? (
                                <S.SubText>Rec&apos;d {formatDate(row.original.receivedDate, 'MMM d')}</S.SubText>
                            ) : null}
                        </S.TableCell>
                    ),
                },
                {
                    id: 'PayerControl',
                    accessor: 'payerControlNumber',
                    width: 165,
                    minWidth: 165,
                    maxWidth: 165,
                    Header: () => <S.TableHeader>Payer control #</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.MonoCell>{row.original.payerControlNumber || '\u2014'}</S.MonoCell>
                    ),
                },
                {
                    id: 'Status',
                    accessor: 'status',
                    width: 110,
                    minWidth: 110,
                    maxWidth: 110,
                    Header: () => <S.TableHeader>Status</S.TableHeader>,
                    // Most of these are NOT denied — a denied line sits on a payment that was otherwise
                    // processed or reversed. The badge is how the drill-down says so.
                    Cell: ({ row }): React.ReactElement => {
                        const badge = paymentStatusBadge(row.original.status);
                        return (
                            <S.StatusBadge tone={badge.tone} title={row.original.status}>
                                {badge.label}
                            </S.StatusBadge>
                        );
                    },
                },
                {
                    id: 'Procedures',
                    accessor: 'allProcedureCodes',
                    width: 115,
                    minWidth: 115,
                    maxWidth: 4000,
                    Header: () => <S.TableHeader>Procedures</S.TableHeader>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.MonoCell>{row.original.allProcedureCodes.join(', ') || '\u2014'}</S.MonoCell>
                    ),
                },
                ...([
                    ['Charge', 'charge', 'Charge'],
                    ['Allowed', 'allowed', 'Allowed'],
                    ['PayerPaid', 'payerPaid', 'Payer paid'],
                    ['PatientResp', 'patientResponsibility', 'Patient resp.'],
                ] as Array<[string, keyof IDenialPaymentRow, string]>).map(([id, key, label]) => ({
                    id,
                    accessor: key,
                    width: 100,
                    minWidth: 100,
                    maxWidth: 100,
                    Header: () => <S.TableHeaderRight>{label}</S.TableHeaderRight>,
                    Cell: ({ row }: { row: { original: IDenialPaymentRow } }): React.ReactElement => (
                        <S.NumberCell>{formatCurrency((row.original[key] as number).toFixed(2))}</S.NumberCell>
                    ),
                })),
                {
                    id: 'MatchedAdj',
                    accessor: 'matchedAmount',
                    width: 110,
                    minWidth: 110,
                    maxWidth: 110,
                    Header: () => <S.TableHeaderRight>{drillCodeRef.current} adj.</S.TableHeaderRight>,
                    Cell: ({ row }): React.ReactElement => (
                        <S.MatchedAmount sign={Math.sign(row.original.matchedAmount)}>
                            {formatCurrency(row.original.matchedAmount.toFixed(2))}
                        </S.MatchedAmount>
                    ),
                },
                {
                    id: 'OpenClaim',
                    accessor: 'id',
                    width: 110,
                    minWidth: 110,
                    maxWidth: 110,
                    Header: () => <span />,
                    Cell: ({ row }): React.ReactElement => (
                        <S.WorkerCell>
                            <BaseButton
                                variant="text"
                                size="compact"
                                onClick={(event: React.MouseEvent): void => {
                                    // The row expands its ERA card; this opens the claim instead.
                                    event.stopPropagation();
                                    openClaimRef.current(row.original);
                                }}>
                                <span>Open claim</span>
                                <Icon path={mdiOpenInNew} size={0.6} />
                            </BaseButton>
                        </S.WorkerCell>
                    ),
                },
            ],
            [],
        );

        // The column header names the row's own code: `CO-50 adj.`. Read through a ref, like the handlers.
        const drillCodeRef = React.useRef('');
        drillCodeRef.current = store?.drillRow ? dashCode(store.drillRow.codes) : '';
        // Paged by the table's own footer (John, FE-116 feedback: "We need pagination on the payment listing
        // view"). The payments are all in memory, so a page is a slice of what the search leaves.
        const [drillWindow, setDrillWindow] = React.useState({ pageIndex: 0, pageSize: 20 });
        const drillAll = store ? store.drillPayments : NO_PAYMENTS;
        const drillMatched = React.useMemo(
            () => (terms.length ? drillAll.filter(payment => paymentRowMatches(payment, terms)) : drillAll),
            [drillAll, terms],
        );
        const drillRows = React.useMemo(
            () =>
                drillMatched.slice(
                    drillWindow.pageIndex * drillWindow.pageSize,
                    (drillWindow.pageIndex + 1) * drillWindow.pageSize,
                ),
            [drillMatched, drillWindow],
        );
        const onDrillFetch = React.useCallback(({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }): void => {
            setDrillWindow(current =>
                current.pageIndex === pageIndex && current.pageSize === pageSize ? current : { pageIndex, pageSize },
            );
        }, []);
        // The table remembers its last page across mounts; another code's payments start on the first.
        const drillTableRef = React.useRef<{ gotoPage: (page: number) => void } | null>(null);
        const onDrillTableReady = React.useCallback((table: TableInstance<IDenialPaymentRow>): void => {
            // The table runs react-table's pagination plugin, which core's react-table types do not declare.
            const paged = table as TableInstance<IDenialPaymentRow> & { gotoPage: (page: number) => void };
            drillTableRef.current = paged;
            paged.gotoPage(0);
        }, []);
        // A new search starts on the first page of what it found.
        React.useEffect(() => {
            drillTableRef.current?.gotoPage(0);
        }, [terms]);
        // The payments behind a row are not searched by the words that found the row: each view starts empty.
        const drillKey = store?.drillRow?.key;
        React.useEffect(() => setSearch(''), [drillKey]);

        const [drillPresenter] = React.useState(
            () =>
                new DataTablePresenter<IDenialPaymentRow>({
                    name: 'denialPayments',
                    data: drillRows,
                    columns: drillColumns,
                    filterSchemas: [],
                }),
        );

        React.useEffect(() => {
            drillPresenter.onDataUpdate(drillRows);
            drillPresenter.onDataTotalChange(drillMatched.length);
        }, [drillPresenter, drillRows, drillMatched.length]);

        // Leaving the Denials tab closes any open drill-down, so returning shows the table.
        React.useEffect(() => {
            if (tab !== 'denials' && store?.drillRow) {
                store.closeDrill();
            }
        }, [tab, store]);

        // Load the rejection report the first time its tab is opened, not on page load: it costs a
        // separate request and most visits never leave the denials table.
        React.useEffect(() => {
            if (tab === 'rejections' && store && !store.rejectionLoading && !store.rejectionLoaded) {
                void store.loadRejections();
            }
        }, [tab, store]);

        // Stable handlers for the tables. Inline arrows were new on every render, so ANY page update — a chip
        // click, a spinner — made the tables re-render every row before the browser could paint.
        const storeRef = React.useRef(store);
        storeRef.current = store;
        const openDrillRow = React.useCallback((row: { original: DenialTableRow }): void => {
            void storeRef.current?.openDrill(row.original);
        }, []);
        const toggleEraCard = React.useCallback((row: unknown): void => {
            (row as { toggleRowExpanded: () => void }).toggleRowExpanded();
        }, []);
        const renderEraCard = React.useCallback(
            (row: { original: IDenialPaymentRow }): React.ReactElement => (
                <EraCard payment={row.original} onOpenClaim={(payment): void => openClaimRef.current(payment)} />
            ),
            [],
        );

        if (!store) {
            return <></>;
        }

        const latestWeekStart = addDays(startOfDay(new Date()), -6);
        const drillCoverage = store.drillRow ? covered.find(row => row.key === store.drillRow?.key)?.coverage : undefined;
        const uncoveredRejections = rejectionEvaluated
            .filter(row => row.coverage && row.coverage.status !== 'configured')
            .reduce((total, row) => total + row.claims, 0);
        const uncoveredAdjustments = covered
            .filter(row => row.coverage && row.coverage.status !== 'configured')
            .reduce((total, row) => total + row.adjustments, 0);

        // Routine groups the filter hid. Counted in the subtitle and named on the control that brings them back,
        // so nothing leaves the table silently.
        const hiddenRoutine = showRoutine ? 0 : store.rows.length - visible.length;
        const showsTable = tab !== 'rejections';
        // Demo mode has no denial data to ask for, so there is nothing to search, re-range or retry.
        const { isDemo } = props;
        // The window the rows CAME from, not the one selected — while a load is in flight the two
        // differ, and a caption naming the new week over the old week's rows is a quiet lie (NS-3).
        const shownPhrase = store.loadedPhrase || store.phrase;
        const selectedWeek = WEEK_BUTTONS.find(
            button => WEEK_PRESET_BY_LABEL[button.label] === store.weekPreset,
        );

        // CSV of what the table shows — its filters and the search applied, every page of it — built from what the
        // page already holds (John, 2026-10-07: "No backend call needed").
        const exportRows = tab === 'rejections' ? rejectionRows.length : store.drillRow ? drillMatched.length : rows.length;
        // The tables carry the rules cell, so a file written before the rules arrive would have it blank while the
        // screen says "Checking…". Waits for them — unless loading them failed, when the file says nothing either way.
        const rulesPending = !rulesReady && !workerStore.error;
        const exportBusy =
            tab === 'rejections'
                ? store.rejectionLoading || !store.rejectionLoadedDays || rulesPending
                : store.drillRow
                ? store.drillLoading || !store.drillDays
                : store.loading || !store.loadedDays || rulesPending;
        // Named after the days the rows CAME from, kept by the store at load time — "This week" moves at midnight.
        const exportCsv = (): void => {
            if (tab === 'rejections' && store.rejectionLoadedDays) {
                const { first, last } = store.rejectionLoadedDays;
                downloadCsvBlob(
                    toCsv(rejectionRows, rejectionsCsvColumns(rejectionRulesRef.current, NO_REJECTION_RULE, store.rejectionType)),
                    denialsCsvFilename('rejections', first, last),
                );
            } else if (tab !== 'rejections' && store.drillRow && store.drillDays) {
                const code = dashCode(store.drillRow.codes);
                const { first, last } = store.drillDays;
                downloadCsvBlob(
                    toCsv(drillMatched, paymentsCsvColumns(code)),
                    denialsCsvFilename('payments', first, last, code, store.drillTruncated),
                );
            } else if (tab !== 'rejections' && !store.drillRow && store.loadedDays) {
                const { first, last } = store.loadedDays;
                downloadCsvBlob(
                    toCsv(rows, denialsCsvColumns(denialRulesRef.current, NO_DENIAL_RULE)),
                    denialsCsvFilename('denials', first, last),
                );
            }
        };
        const exportButton = (
            <BaseButton
                variant="outlined"
                size="compact"
                aria-label="Export what the table shows as CSV"
                disabled={exportBusy || exportRows === 0}
                onClick={exportCsv}>
                <Icon path={mdiTrayArrowDown} size={0.6} />
                <span>Export</span>
            </BaseButton>
        );

        return (
            <PageLayout>
                {/* v3 has no title row and no header buttons: the tabs ARE the header. The Workers page is in
                    the sidebar, and Investigate and Export had nothing behind them. */}
                {/* The reworked design has no stat cards: checked against the handoff markup, which carries
                    none. They were an older draft's, and two of the three — a filing deadline and worker
                    coverage — could never be filled from anything the platform returns. A row of permanent
                    dashes is worse than no row. */}

                <PageLayoutRow>
                    <TabContainer
                        variant="underline"
                        tabs={TABS}
                        activeTab={tab}
                        onTabChange={(id): void => {
                            setTab(id as DenialsTab);
                            setSearch('');
                            // Each tab counts its own coverage, so a filter chosen on one means nothing on the other.
                            resetFilter();
                        }}
                        panelId={TAB_PANEL_ID}
                        trailing={
                            // Kept when a load fails: a shorter range is one way out of a report that failed.
                            isDemo ? undefined : showsTable ? (
                                <S.Toolbar>
                                    <DenialsSearch
                                        value={search}
                                        onChange={setSearch}
                                        placeholder={
                                            store.drillRow
                                                ? 'Search payer control #, procedure or code'
                                                : 'Search CARC, RARC or description'
                                        }
                                    />
                                    <ButtonGroup
                                        variant="track"
                                        buttons={WEEK_BUTTONS}
                                        selected={selectedWeek}
                                        onSelect={(button): void => {
                                            const preset = WEEK_PRESET_BY_LABEL[button.label];
                                            if (preset) {
                                                store.setWeekPreset(preset);
                                            }
                                        }}
                                    />
                                    <S.WeekPickerSlot>
                                        <S.WeekPickerLabel htmlFor="denials-week-picker">
                                            Week starting
                                        </S.WeekPickerLabel>
                                        <BaseInput
                                            compact
                                            disableLatin1Validation
                                            id="denials-week-picker"
                                            type="date"
                                            aria-label="Pick the first day of the week to show"
                                            // The latest week that can start is the one ending today: a later start
                                            // would reach into days with no data.
                                            max={formatDate(latestWeekStart, 'yyyy-MM-dd')}
                                            value={formatDate(addDays(store.lastDay, -6), 'yyyy-MM-dd')}
                                            onChange={(event): void => {
                                                const picked = event.target.valueAsDate;
                                                if (!picked) {
                                                    return;
                                                }
                                                // valueAsDate is UTC midnight; read the parts back so the week is
                                                // the one the operator clicked in their own zone.
                                                const start = new Date(
                                                    picked.getUTCFullYear(),
                                                    picked.getUTCMonth(),
                                                    picked.getUTCDate(),
                                                );
                                                // Capped at this week, as the design caps it.
                                                if (start >= latestWeekStart) {
                                                    store.setWeekPreset('thisWeek');
                                                } else {
                                                    store.pickWeek(addDays(start, 6));
                                                }
                                            }}
                                        />
                                    </S.WeekPickerSlot>
                                    <S.RangeLabel>{shownPhrase}</S.RangeLabel>
                                    {exportButton}
                                </S.Toolbar>
                            ) : (
                                <S.Toolbar>
                                    <DenialsSearch
                                        value={search}
                                        onChange={setSearch}
                                        placeholder="Search message or status code"
                                    />
                                    <S.TypeSelectSlot>
                                        <BaseSelect
                                            aria-label="Status type"
                                            options={STATUS_TYPE_OPTIONS}
                                            value={STATUS_TYPE_OPTIONS.find(option => option.value === store.rejectionType)}
                                            onChange={(option): void => {
                                                const value = (option as ISelectOption | null)?.value;
                                                if (value) {
                                                    // Other types are not evaluated at all; a coverage filter
                                                    // chosen under REJECTED would empty the table.
                                                    resetFilter();
                                                    store.setRejectionType(value as RejectionStatusChoice);
                                                }
                                            }}
                                            isSearchable={false}
                                            isClearable={false}
                                        />
                                    </S.TypeSelectSlot>
                                    <ButtonGroup
                                        variant="track"
                                        buttons={WEEK_BUTTONS}
                                        selected={WEEK_BUTTONS.find(
                                            button => WEEK_PRESET_BY_LABEL[button.label] === store.rejectionWeekPreset,
                                        )}
                                        onSelect={(button): void => {
                                            const preset = WEEK_PRESET_BY_LABEL[button.label];
                                            if (preset) {
                                                store.setRejectionWeekPreset(preset);
                                            }
                                        }}
                                    />
                                    <S.WeekPickerSlot>
                                        <S.WeekPickerLabel htmlFor="rejections-week-picker">Week starting</S.WeekPickerLabel>
                                        <BaseInput
                                            compact
                                            disableLatin1Validation
                                            id="rejections-week-picker"
                                            type="date"
                                            aria-label="Pick the first day of the rejections week"
                                            max={formatDate(latestWeekStart, 'yyyy-MM-dd')}
                                            value={formatDate(addDays(store.rejectionLastDay, -6), 'yyyy-MM-dd')}
                                            onChange={(event): void => {
                                                const picked = event.target.valueAsDate;
                                                if (!picked) {
                                                    return;
                                                }
                                                const start = new Date(
                                                    picked.getUTCFullYear(),
                                                    picked.getUTCMonth(),
                                                    picked.getUTCDate(),
                                                );
                                                if (start >= latestWeekStart) {
                                                    store.setRejectionWeekPreset('thisWeek');
                                                } else {
                                                    store.pickRejectionWeek(addDays(start, 6));
                                                }
                                            }}
                                        />
                                    </S.WeekPickerSlot>
                                    <S.RangeLabel>{store.rejectionPhrase}</S.RangeLabel>
                                    {exportButton}
                                </S.Toolbar>
                            )
                        }
                    />
                </PageLayoutRow>

                <PageLayoutRow>
                    <S.TabPanel role="tabpanel" id={TAB_PANEL_ID} aria-labelledby={tabElementId(TAB_PANEL_ID, tab)}>
                        {showsTable && store.drillRow ? (
                            <DataTableCard size="large">
                                <S.DrillHeader>
                                    <BaseButton
                                        variant="text"
                                        size="small"
                                        aria-label="Back to all combinations"
                                        onClick={store.closeDrill}>
                                        <Icon path={mdiArrowLeft} size={0.8} />
                                    </BaseButton>
                                    <S.CodeRemarkCell>
                                        <S.CodeChip>{dashCode(store.drillRow.codes)}</S.CodeChip>
                                        {store.drillRow.remarkCodes.map(code => (
                                            <S.RemarkChip key={code}>{code}</S.RemarkChip>
                                        ))}
                                    </S.CodeRemarkCell>
                                    <S.DrillTitle>
                                        <S.ReasonMain>
                                            <strong>{store.drillRow.reasonDescription}</strong>
                                        </S.ReasonMain>
                                        {/* The count will not match the row's adjustment figure, and is not meant
                                            to; the tooltip says why rather than leaving it to read as a bug. */}
                                        <S.SummarySub title={DRILL_COUNT_NOTE}>
                                            {store.drillLoading
                                                ? 'Loading the payments\u2026'
                                                : `${store.drillTotals.payments.toLocaleString()} ${
                                                      store.drillTotals.payments === 1 ? 'payment' : 'payments'
                                                  } with this adjustment \u00b7 newest first \u00b7 ${shownPhrase}${
                                                      store.drillTruncated
                                                          ? ` \u00b7 the first ${PAYMENT_ROW_CAP.toLocaleString()} payment rows only \u2014 more exist`
                                                          : ''
                                                  }${
                                                      terms.length
                                                          ? ` \u00b7 ${drillMatched.length.toLocaleString()} match the search`
                                                          : ''
                                                  }`}
                                        </S.SummarySub>
                                    </S.DrillTitle>
                                    {drillCoverage && (
                                        <S.CoverageBadge status={drillCoverage.status} title={COVERAGE_ESTIMATE}>
                                            {COVERAGE_BADGE_TEXT[drillCoverage.status]}
                                        </S.CoverageBadge>
                                    )}
                                    <BaseButton
                                        variant={drillCoverage?.status === 'configured' ? 'outlined' : 'primary'}
                                        size="compact"
                                        onClick={(): void => {
                                            const drillRow = covered.find(row => row.key === store.drillRow?.key);
                                            if (drillRow) {
                                                openDenialEditor(drillRow);
                                            }
                                        }}>
                                        <Icon path={mdiRobotOutline} size={0.6} />
                                        <span>{drillCoverage?.status === 'configured' ? 'Edit worker' : 'Configure worker'}</span>
                                    </BaseButton>
                                </S.DrillHeader>
                                {store.drillError ? (
                                    <S.NoticeSlot>
                                        <Notice tone="attention">{store.drillError}</Notice>
                                    </S.NoticeSlot>
                                ) : (
                                    <S.TableFrame>
                                        <DataTable
                                            presenter={drillPresenter}
                                            isLoading={store.drillLoading}
                                            // A row opens its ERA payment card in place; Open claim opens the claim.
                                            onRowClick={toggleEraCard}
                                            renderRowSubComponent={renderEraCard}
                                            onFetch={onDrillFetch}
                                            onTableReady={onDrillTableReady}
                                            disableSorting={true}
                                            hideFiltering={true}
                                            hideSettings={true}
                                        />
                                    </S.TableFrame>
                                )}
                            </DataTableCard>
                        ) : showsTable ? (
                            <>
                            <S.SummaryRow>
                                <div>
                                    <S.SummaryHead>
                                        {store.loading ? (
                                            // Up here, where the eye is: the table's own loader centres on the
                                            // whole table, which is taller than the screen, so it was off-view.
                                            <S.LoadingLine role="status">
                                                <Spinner size="16px" />
                                                Loading {store.phrase}&hellip;
                                            </S.LoadingLine>
                                        ) : store.error || !rulesReady
                                            ? 'Adjustments by reason and remark code'
                                            : `${counts.configured} of ${counts.all} adjustment + remark ${
                                                  counts.all === 1 ? 'combination has' : 'combinations have'
                                              } a Denial Worker rule`}
                                    </S.SummaryHead>
                                    <S.SummarySub hidden={store.loading}>
                                        {/* Counts only, no netted dollar figure: amounts are signed, and a sum
                                            makes a takeback disappear into the denials around it. */}
                                        {!store.error && rulesReady && `${uncoveredAdjustments.toLocaleString()} ${
                                            uncoveredAdjustments === 1 ? 'adjustment has' : 'adjustments have'
                                        } no rule or only a partial one · `}
                                        {shownPhrase}
                                        {rulesReady && (
                                            <>
                                                {' · '}
                                                <S.Estimate title={COVERAGE_ESTIMATE}>coverage estimated from the saved rules</S.Estimate>
                                            </>
                                        )}
                                        {!rulesReady && workerStore.error && ` · worker rules could not be loaded: ${workerStore.error}`}
                                        {!store.error &&
                                            hiddenRoutine > 0 &&
                                            ` · ${hiddenRoutine} routine ${
                                                hiddenRoutine === 1 ? 'combination' : 'combinations'
                                            } hidden`}
                                        {!store.error &&
                                            store.skipped > 0 &&
                                            ` · ${store.skipped} report ${
                                                store.skipped === 1 ? 'row' : 'rows'
                                            } could not be read as a denial code`}
                                    </S.SummarySub>
                                </div>
                                <S.SummaryControls>
                                    <BaseCheckbox
                                        variant="small"
                                        checked={shownRoutine}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                                            chooseRoutine(e.target.checked)
                                        }
                                        label={`Show routine adjustments (${ROUTINE_ADJUSTMENT_LABEL})`}
                                    />
                                    {rulesReady && !store.error && !store.loading && (
                                        <TypeFilterBar>
                                            {COVERAGE_CHIPS.map(chip => (
                                                <TypeFilterChip
                                                    key={chip.filter}
                                                    active={shownFilter === chip.filter}
                                                    onClick={(): void => chooseFilter(chip.filter)}>
                                                    {chip.filter !== 'all' && <S.CoverageDot status={chip.filter} />}
                                                    {chip.label}
                                                    <TypeCountBadge active={shownFilter === chip.filter}>
                                                        {/* The chip just clicked shows the work in place of its count. */}
                                                        {filterBusy && shownFilter === chip.filter ? (
                                                            <S.ChipSpinner role="status" aria-label="Filtering">
                                                                <Spinner size="12px" />
                                                            </S.ChipSpinner>
                                                        ) : (
                                                            chipCounts[chip.filter]
                                                        )}
                                                    </TypeCountBadge>
                                                </TypeFilterChip>
                                            ))}
                                        </TypeFilterBar>
                                    )}
                                </S.SummaryControls>
                            </S.SummaryRow>
                            <DataTableCard size="large">
                                {store.error ? (
                                    <S.NoticeSlot>
                                        <Notice tone="attention">
                                            <S.NoticeLine>
                                                <span>{store.error}</span>
                                                {!isDemo && (
                                                    <BaseButton
                                                        variant="outlined"
                                                        size="compact"
                                                        onClick={(): void => {
                                                            void store.load();
                                                        }}>
                                                        Try again
                                                    </BaseButton>
                                                )}
                                            </S.NoticeLine>
                                        </Notice>
                                    </S.NoticeSlot>
                                ) : (
                                    <S.TableFrame stale={store.loading || filterBusy} aria-busy={store.loading || filterBusy}>
                                        <DataTable
                                            presenter={presenter}
                                            isLoading={store.loading}
                                            // A group row opens the payments behind it — the design's
                                            // drill-down. The week's payments are fetched once.
                                            onRowClick={openDrillRow}
                                            // The rows come biggest first, and nothing here can re-sort them: the
                                            // table sorts through a fetch this page does not have. Left on, a header
                                            // click would show a sort arrow over rows that did not move.
                                            disableSorting={true}
                                            hideFiltering={true}
                                            // Without this the table draws an empty controls panel above its header —
                                            // the blank band between the section title and the columns.
                                            hideNavigation={true}
                                            hidePagination={true}
                                            hideSettings={true}
                                        />
                                    </S.TableFrame>
                                )}

                            </DataTableCard>
                            </>
                        ) : (
                            <>
                            <S.SummaryRow>
                                <div>
                                    <S.SummaryHead>
                                        {store.rejectionLoading ? (
                                            <S.LoadingLine role="status">
                                                <Spinner size="16px" />
                                                Loading {store.rejectionPhrase}&hellip;
                                            </S.LoadingLine>
                                        ) : store.rejectionType !== 'REJECTED' ? (
                                            `${store.rejectionRows.length.toLocaleString()} status ${
                                                  store.rejectionRows.length === 1 ? 'message' : 'messages'
                                              } \u00b7 ${store.rejectionType === 'ALL' ? 'all status types' : store.rejectionType}`
                                        ) : !rulesReady || store.rejectionError
                                            ? 'Rejections by status message'
                                            : `${rejectionCounts.configured} of ${rejectionCounts.all} rejection ${
                                                  rejectionCounts.all === 1 ? 'message matches' : 'messages match'
                                              } a Rejection Worker rule`}
                                    </S.SummaryHead>
                                    <S.SummarySub hidden={store.rejectionLoading}>
                                        {/* A count only: charges overlap across messages, so a charge total for
                                            the uncovered ones would overstate them. */}
                                        {store.rejectionType === 'REJECTED' &&
                                            rulesReady &&
                                            !store.rejectionError &&
                                            `${uncoveredRejections.toLocaleString()} ${
                                                uncoveredRejections === 1 ? 'rejection has' : 'rejections have'
                                            } no rule · `}
                                        {store.rejectionType !== 'REJECTED' && `${NOT_EVALUATED} · `}
                                        {store.rejectionPhrase}
                                        {store.rejectionType === 'REJECTED' && rulesReady && (
                                            <>
                                                {' · '}
                                                <S.Estimate title={COVERAGE_ESTIMATE}>coverage estimated from the saved rules</S.Estimate>
                                            </>
                                        )}
                                        {store.rejectionBlank > 0 &&
                                            ` · ${store.rejectionBlank.toLocaleString()} with no message, counted but not listed`}
                                    </S.SummarySub>
                                </div>
                                {store.rejectionType === 'REJECTED' && rulesReady && !store.rejectionError && !store.rejectionLoading && (
                                    <S.SummaryControls>
                                        <TypeFilterBar>
                                            {COVERAGE_CHIPS.filter(
                                                chip => chip.filter !== 'partial' || rejectionCounts.partial > 0,
                                            ).map(chip => (
                                                <TypeFilterChip
                                                    key={chip.filter}
                                                    active={shownFilter === chip.filter}
                                                    onClick={(): void => chooseFilter(chip.filter)}>
                                                    {chip.filter !== 'all' && <S.CoverageDot status={chip.filter} />}
                                                    {chip.label}
                                                    <TypeCountBadge active={shownFilter === chip.filter}>
                                                        {filterBusy && shownFilter === chip.filter ? (
                                                            <S.ChipSpinner role="status" aria-label="Filtering">
                                                                <Spinner size="12px" />
                                                            </S.ChipSpinner>
                                                        ) : (
                                                            rejectionChipCounts[chip.filter]
                                                        )}
                                                    </TypeCountBadge>
                                                </TypeFilterChip>
                                            ))}
                                        </TypeFilterBar>
                                    </S.SummaryControls>
                                )}
                            </S.SummaryRow>
                            <DataTableCard size="large">
                                {store.rejectionError ? (
                                    <S.NoticeSlot>
                                        <Notice tone="attention">{store.rejectionError}</Notice>
                                    </S.NoticeSlot>
                                ) : (
                                    <S.TableFrame stale={filterBusy} aria-busy={filterBusy}>
                                        <DataTable
                                            presenter={rejectionPresenter}
                                            isLoading={store.rejectionLoading}
                                            disableSorting={true}
                                            hideFiltering={true}
                                            hideNavigation={true}
                                            hidePagination={true}
                                            hideSettings={true}
                                        />
                                    </S.TableFrame>
                                )}
                            </DataTableCard>
                            </>
                        )}
                    </S.TabPanel>
                </PageLayoutRow>
                {store?.drillRow && store.drawerPayment && (
                    <ClaimDrawer
                        store={store}
                        row={store.drillRow}
                        claimHref={claimHref}
                        privatePayUnavailable={privatePayUnavailable}
                        onClose={store.closeClaim}
                        onConfigureWorker={(): void => {
                            if (store.drillRow) {
                                openDenialEditorRef.current(store.drillRow);
                            }
                        }}
                        onCloseOut={closeOutClaim}
                        onCreateTask={createTaskForClaim}
                        onEditClaim={onEditClaim}
                    />
                )}
            </PageLayout>
        );
    },
);
